import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Lock, Plus, Trash2, Edit3, ExternalLink, Copy, Save, X, RefreshCw, Upload } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'uandfashion_admin_key';

const EMPTY_PRODUCT = { brand_name: '', affiliate_link: '', image_url: '', note: '' };
const EMPTY_FORM = {
  slug: '',
  title: '',
  intro_nl: '',
  intro_en: '',
  hero_image: '',
  products: [{ ...EMPTY_PRODUCT }],
  published: true,
};

const AdminLooksPage = () => {
  const [keyInput, setKeyInput] = useState('');
  const [authKey, setAuthKey] = useState(() => sessionStorage.getItem(STORAGE_KEY) || '');
  const [looks, setLooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null); // null when creating
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingIdx, setUploadingIdx] = useState(null); // product image upload index
  const heroInputRef = useRef(null);
  const productInputRefs = useRef({});

  // Upload a file to the backend and return the absolute URL to store.
  const uploadFile = async (file) => {
    if (!file) return null;
    if (file.size > 8 * 1024 * 1024) {
      setError('Afbeelding is groter dan 8 MB.');
      return null;
    }
    const data = new FormData();
    data.append('file', file);
    const res = await axios.post(`${API}/uploads`, data, {
      params: { key: authKey },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return `${process.env.REACT_APP_BACKEND_URL}${res.data.url}`;
  };

  const handleHeroFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploadingHero(true);
    try {
      const url = await uploadFile(file);
      if (url) setForm((f) => ({ ...f, hero_image: url }));
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload mislukt.');
    } finally {
      setUploadingHero(false);
      if (heroInputRef.current) heroInputRef.current.value = '';
    }
  };

  const handleProductFile = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploadingIdx(idx);
    try {
      const url = await uploadFile(file);
      if (url) updateProduct(idx, 'image_url', url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Upload mislukt.');
    } finally {
      setUploadingIdx(null);
      if (productInputRefs.current[idx]) productInputRefs.current[idx].value = '';
    }
  };

  const fetchList = useCallback(async (key) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/looks/admin/list`, { params: { key } });
      setLooks(res.data || []);
      sessionStorage.setItem(STORAGE_KEY, key);
      setAuthKey(key);
    } catch (err) {
      setError(
        err?.response?.status === 403
          ? 'Onjuist wachtwoord — probeer opnieuw.'
          : 'Kan de lijst niet ophalen.'
      );
      sessionStorage.removeItem(STORAGE_KEY);
      setAuthKey('');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authKey) fetchList(authKey);
    // Support ?key= autologin
    const params = new URLSearchParams(window.location.search);
    const urlKey = params.get('key');
    if (urlKey && !authKey) fetchList(urlKey);
  }, []);

  const handleAuth = (e) => {
    e.preventDefault();
    if (keyInput.trim()) fetchList(keyInput.trim());
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (look) => {
    setEditingId(look.id);
    setForm({
      slug: look.slug || '',
      title: look.title || '',
      intro_nl: look.intro_nl || '',
      intro_en: look.intro_en || '',
      hero_image: look.hero_image || '',
      products: look.products?.length ? look.products : [{ ...EMPTY_PRODUCT }],
      published: look.published !== false,
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const updateProduct = (idx, field, value) => {
    setForm((f) => ({
      ...f,
      products: f.products.map((p, i) => (i === idx ? { ...p, [field]: value } : p)),
    }));
  };

  const addProduct = () => {
    setForm((f) => ({ ...f, products: [...f.products, { ...EMPTY_PRODUCT }] }));
  };

  const removeProduct = (idx) => {
    setForm((f) => ({ ...f, products: f.products.filter((_, i) => i !== idx) }));
  };

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        products: form.products.filter((p) => p.brand_name.trim() && p.affiliate_link.trim()),
      };
      if (editingId) {
        await axios.patch(`${API}/looks/${editingId}`, payload, { params: { key: authKey } });
      } else {
        await axios.post(`${API}/looks`, payload, { params: { key: authKey } });
      }
      closeForm();
      fetchList(authKey);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Opslaan mislukt.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (look) => {
    if (!window.confirm(`Verwijder "${look.title}"? Dit kan niet ongedaan gemaakt worden.`)) return;
    try {
      await axios.delete(`${API}/looks/${look.id}`, { params: { key: authKey } });
      fetchList(authKey);
    } catch {
      setError('Verwijderen mislukt.');
    }
  };

  const copyShareUrl = async (slug) => {
    const url = `https://uandfashion.eu/look/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(slug);
      setTimeout(() => setCopiedId(null), 1500);
    } catch { /* clipboard blocked */ }
  };

  // ---- Auth screen ----------------------------------------------------------
  if (!authKey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50/40 to-indigo-50/40 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm p-10">
          <div className="text-center mb-6">
            <Lock className="w-8 h-8 text-purple-500 mx-auto mb-3" />
            <h1 className="text-2xl font-medium text-gray-900">Beheer — Looks</h1>
            <p className="text-sm text-gray-500 mt-2">Toegang met beheerder-sleutel.</p>
          </div>
          <form onSubmit={handleAuth} className="space-y-4">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Beheerder-sleutel"
              className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent outline-none"
              data-testid="looks-admin-key-input"
              autoFocus
            />
            <button
              type="submit"
              className="w-full bg-gray-900 text-white py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors"
              data-testid="looks-admin-submit"
            >
              Ontgrendel
            </button>
            {error && <p className="text-sm text-red-600 text-center">{error}</p>}
          </form>
        </div>
      </div>
    );
  }

  // ---- Main dashboard -------------------------------------------------------
  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-looks-page">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-light text-gray-900">Looks beheer</h1>
            <p className="text-sm text-gray-500 mt-1">
              Beheer je &quot;Shop de Look&quot;-pagina&apos;s voor Instagram &amp; Facebook Reels.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => fetchList(authKey)}
              className="inline-flex items-center gap-2 bg-white border border-gray-200 text-gray-700 px-4 py-2 rounded-full text-sm hover:bg-gray-50 transition-colors"
              data-testid="looks-refresh"
            >
              <RefreshCw className="w-4 h-4" />
              Ververs
            </button>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-pink-600 transition-colors"
              data-testid="looks-add-btn"
            >
              <Plus className="w-4 h-4" />
              Nieuwe look
            </button>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">{error}</div>}

        {loading ? (
          <div className="text-center py-16 text-gray-500">Laden…</div>
        ) : looks.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center text-gray-500 border border-gray-100">
            <p className="mb-4">Nog geen looks. Voeg je eerste &quot;Shop de Look&quot; toe om te delen op Instagram.</p>
            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-pink-600 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Eerste look toevoegen
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="looks-grid">
            {looks.map((look) => (
              <div
                key={look.id}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col"
                data-testid={`look-card-${look.slug}`}
              >
                <div className="aspect-video bg-gray-100 overflow-hidden">
                  {look.hero_image && (
                    <img src={look.hero_image} alt={look.title} className="w-full h-full object-cover" loading="lazy" />
                  )}
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h3 className="font-medium text-gray-900 leading-tight">{look.title}</h3>
                    {!look.published && (
                      <span className="text-[10px] uppercase tracking-wider bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full shrink-0">
                        draft
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    /look/{look.slug} · {look.products?.length || 0} product{look.products?.length === 1 ? '' : 'en'}
                  </p>
                  <div className="mt-auto pt-3 border-t border-gray-100 flex flex-wrap gap-2">
                    <button
                      onClick={() => copyShareUrl(look.slug)}
                      className="inline-flex items-center gap-1.5 text-xs bg-gray-50 text-gray-700 px-2.5 py-1.5 rounded-full hover:bg-gray-100"
                      data-testid={`look-copy-${look.slug}`}
                    >
                      <Copy className="w-3 h-3" />
                      {copiedId === look.slug ? 'Gekopieerd!' : 'Kopieer link'}
                    </button>
                    <a
                      href={`/look/${look.slug}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs bg-gray-50 text-gray-700 px-2.5 py-1.5 rounded-full hover:bg-gray-100"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Preview
                    </a>
                    <button
                      onClick={() => openEdit(look)}
                      className="inline-flex items-center gap-1.5 text-xs bg-gray-900 text-white px-2.5 py-1.5 rounded-full hover:bg-pink-600 transition"
                      data-testid={`look-edit-${look.slug}`}
                    >
                      <Edit3 className="w-3 h-3" />
                      Bewerk
                    </button>
                    <button
                      onClick={() => handleDelete(look)}
                      className="inline-flex items-center gap-1.5 text-xs text-red-600 hover:bg-red-50 px-2.5 py-1.5 rounded-full"
                      data-testid={`look-delete-${look.slug}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal form */}
      {showForm && (
        <div
          className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 overflow-y-auto py-8 px-4"
          onClick={closeForm}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-2xl w-full my-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 p-5">
              <h2 className="text-xl font-medium text-gray-900">
                {editingId ? 'Look bewerken' : 'Nieuwe look'}
              </h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submitForm} className="p-5 space-y-5 max-h-[70vh] overflow-y-auto" data-testid="looks-form">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titel *</label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Cosy Teddy Vibes"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-400 outline-none"
                  data-testid="looks-form-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL-slug <span className="text-gray-400 font-normal">(optioneel — anders automatisch uit titel)</span>
                </label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="cosy-teddy-vibes"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-400 outline-none font-mono text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hero-afbeelding *</label>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="url"
                    required
                    value={form.hero_image}
                    onChange={(e) => setForm({ ...form, hero_image: e.target.value })}
                    placeholder="URL of upload een bestand →"
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-400 outline-none text-sm"
                    data-testid="looks-form-hero"
                  />
                  <button
                    type="button"
                    onClick={() => heroInputRef.current?.click()}
                    disabled={uploadingHero}
                    className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-pink-600 transition-colors disabled:opacity-50 shrink-0"
                    data-testid="looks-form-hero-upload"
                  >
                    <Upload className="w-4 h-4" />
                    {uploadingHero ? 'Uploaden…' : 'Upload foto'}
                  </button>
                  <input
                    ref={heroInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    onChange={handleHeroFile}
                    className="hidden"
                  />
                </div>
                {form.hero_image && (
                  <img src={form.hero_image} alt="preview" className="mt-2 w-32 h-32 object-cover rounded-lg border" />
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intro NL</label>
                  <textarea
                    rows={3}
                    value={form.intro_nl}
                    onChange={(e) => setForm({ ...form, intro_nl: e.target.value })}
                    placeholder="Deze warme teddy-jas is perfect voor koude herfstdagen."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-400 outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Intro EN</label>
                  <textarea
                    rows={3}
                    value={form.intro_en}
                    onChange={(e) => setForm({ ...form, intro_en: e.target.value })}
                    placeholder="This warm teddy jacket is perfect for cold autumn days."
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-400 outline-none text-sm"
                  />
                </div>
              </div>

              {/* Products */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Producten ({form.products.length})
                  </label>
                  <button
                    type="button"
                    onClick={addProduct}
                    className="text-xs inline-flex items-center gap-1 text-purple-600 hover:text-purple-800"
                    data-testid="looks-form-add-product"
                  >
                    <Plus className="w-3 h-3" />
                    Product toevoegen
                  </button>
                </div>
                <div className="space-y-3">
                  {form.products.map((product, idx) => (
                    <div key={idx} className="bg-gray-50 border border-gray-100 rounded-lg p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Product {idx + 1}</span>
                        {form.products.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeProduct(idx)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <input
                          type="text"
                          placeholder="Merknaam (bv. Urban Bliss)"
                          value={product.brand_name}
                          onChange={(e) => updateProduct(idx, 'brand_name', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-400"
                        />
                        <input
                          type="url"
                          placeholder="Affiliate link"
                          value={product.affiliate_link}
                          onChange={(e) => updateProduct(idx, 'affiliate_link', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-400"
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <input
                          type="url"
                          placeholder="Afbeelding URL (optioneel)"
                          value={product.image_url}
                          onChange={(e) => updateProduct(idx, 'image_url', e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-400"
                        />
                        <button
                          type="button"
                          onClick={() => productInputRefs.current[idx]?.click()}
                          disabled={uploadingIdx === idx}
                          className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 px-3 py-2 rounded-lg text-xs hover:bg-gray-50 shrink-0 disabled:opacity-50"
                        >
                          <Upload className="w-3.5 h-3.5" />
                          {uploadingIdx === idx ? 'Uploaden…' : 'Upload'}
                        </button>
                        <input
                          ref={(el) => { productInputRefs.current[idx] = el; }}
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          onChange={(e) => handleProductFile(idx, e)}
                          className="hidden"
                        />
                      </div>
                      <input
                        type="text"
                        placeholder="Korte notitie (optioneel)"
                        value={product.note}
                        onChange={(e) => updateProduct(idx, 'note', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-purple-400"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="rounded"
                />
                Publiceer meteen (zichtbaar op site en Instagram hub)
              </label>

              <div className="flex gap-3 pt-3 border-t border-gray-100">
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-2 bg-gray-900 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-pink-600 transition-colors disabled:opacity-50"
                  data-testid="looks-form-submit"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Opslaan…' : editingId ? 'Wijzigingen opslaan' : 'Look aanmaken'}
                </button>
                <button
                  type="button"
                  onClick={closeForm}
                  className="text-sm text-gray-500 hover:text-gray-700 px-4"
                >
                  Annuleren
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLooksPage;
